import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddButton from '../../components/common/AddButton/AddButton';
import Filter from '../../components/common/Filter/Filter';
import Sort from '../../components/common/Sort/Sort';
import AddUserRolePopup from '../../components/Popup/AddPopup/AddInternalUserRoles';
import ArchivePopup from '../../components/Popup/ArchivePopup/ArchivePopup';
import EditIcon from '../../components/Table/EditIcon/EditIcon';
import ArchiveIcon from '../../components/Table/ArchiveIcon/ArchiveIcon';
import ExportLabel from '../../components/common/ExportLabel/Export';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import DetailIcon from '../../components/Table/DetailIcon/DetailIcon';
import EditInternalUserRolesPopup from '../../components/Popup/EditPopup/EditInternalUserRoles';
import InternalUserDetailPopup from '../../components/Popup/DetailPopup/InternalUserDetail';
import '../../pages/Settings/Settings.css';
import SimpleSearchBox from '../../components/common/Search/SimpleSearch';
import { baseurl } from '../../api';
import { isRequired, isTextOnly } from '../../validation';
import { useCheckboxClick } from '../../checkboxclick';
import ArchiveSelectedItem from '../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import authenticatedAxios from '../../authenticatedAxios';
import useFilterStore from '../../store/useFilterStore';
import DeletePopup from '../../components/Popup/DeletePopup/DeletePopup';
import DeleteIcon from '../../components/Table/DeleteIcon/DeleteIcon';
import useRefreshStore from '../../store/useRefreshStore';
const pageKey = "userroles"

const UserRoles = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [isNewPopupOpen, setIsNewPopupOpen] = useState(false);
  const [isDetailPopupOpen, setIsDetailPopupOpen] = useState(false); 
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Unarchived");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");
  const [selectedItem, setSelectedItem] = useState(null);
  const [initialTableRows, setData]=useState([]);
  const navigate = useNavigate();
  const [popupError, setPopupError] = useState('');
  const sortOptions = ['A-Z', 'Z-A', 'Newest','Oldest'];
  const filterOptions = ['Default', 'Archived', 'Unarchived'];
  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState, } = useCheckboxClick(items);
  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["User Roles"] || 0;

  const fetchUserroles = async (id = null) => {
    try {
      if (id) {
        const response = await authenticatedAxios.get(`${baseurl}accounts/roles/organizational/?id=${id}`);
        
        if (response && response.data.response_code === 1) {
          setSelectedItem(response.data.data);
        } else {
          console.error("No data found for the selected user role.");
        }
      } else {
        const params = {
          search: searchParams.term,
          filter: selectedFilterOption === 'Default' ? undefined : selectedFilterOption.toLowerCase(),
          sort: mapSortOptionToQuery(selectedSortOption),
        };
  
        const response = await authenticatedAxios.get(`${baseurl}accounts/roles/organizational/`, { params });
  
        if (response && response.data.response_code === 1) {
          setItems(response.data.data);
        }
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      } else {
        console.error('Error fetching user roles:', error);
      }
    }
  };
  
  
  useEffect(() => {
    fetchUserroles();
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
  });
  }, [searchParams, selectedFilterOption, selectedSortOption]); 
  
  useEffect(() => {
    fetchUserroles();
  }, [refreshKey]);
  

  const fetchData = async () => {
    try {
        const response = await authenticatedAxios.get(`${baseurl}accounts/permissions/?is_company=true`);

        if (response.data) {
            setData(response.data); 
            
        } else {
            console.error('Error fetching data: Data is empty');
        }
    } catch (error) {
        console.error('Error fetching data:', error);
    }
  };
  
  const AddUserRole = async (formData) => {
    try {
      const response = await authenticatedAxios.post(
        `${baseurl}accounts/roles/organizational/`,
        formData
      );
  
      if (response.data.response_code === 1) {
        await fetchUserroles();
        closePopups();
      } else {
        setPopupError(response.data.message || 'Something went wrong!');
      }
    } catch (error) {
      setPopupError('Error adding user role. Please try again.');
    }
  };
  
  const handleEdit = async (formData) => {
    try {
      const response = await authenticatedAxios.put(
        `${baseurl}accounts/roles/organizational/?id=${selectedItem.id}`,
        formData
      );
  
      if (response.data.response_code === 1) {
        await fetchUserroles();
        closePopups();
      } else {
        setPopupError(response.data.message || 'Failed to edit user role.');
      }
    } catch (error) {
      setPopupError('Error editing user role. Please try again.');
    }
  };
  

  const handleDelete = async () => {
    try {
      const response = await authenticatedAxios.delete(
        `${baseurl}accounts/roles/organizational/?id=${selectedItem.id}`
      );
  
      if (response.status >= 200 && response.status < 300) {
        setIsDeletePopupOpen(false); 
        fetchUserroles(); 
        return;
      }
      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      // console.error('Error deleting plan:', error);
    }
  };

  const handleArchiveSelectedItems = async () => {
    try {
      const response = await authenticatedAxios.post(`${baseurl}accounts/roles/archive/`, {
        id_list: selectedItems,
      });

      if (response.status >= 200 && response.status < 300) {
        fetchUserroles();
        resetCheckboxState();
        closePopups();
      } 
    } catch (error) {
      console.error('Error archiving/unarchiving selected items:', error);
    }
  };

  const handleArchive = async () => {
    try {
      const response = await authenticatedAxios.get(
        `${baseurl}accounts/roles/archive/?id=${selectedItem.id}`
      );
      
      if (response.status >= 200 && response.status < 300) {
        await fetchUserroles(); 
        closePopups();  
      } 
    } catch (error) {
    }
  };

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z':
        return 'a_to_z';
      case 'Z-A':
        return 'z_to_a';
      case 'Newest':
        return 'newest';
      case 'Oldest':
        return 'oldest';
      default:
        return 'newest';
    }
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const handleSearchSelect = (searchTerm) => {
    setSearchParams({ term: searchTerm });
  };


  const handleEditClick = (item) => {
    fetchData();
    setSelectedItem(item);
    setIsEditPopupOpen(true);
  };


  const handleArchiveClick = async (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };

  const handleNewClick = () => {
    setSelectedItem(null);
    setIsNewPopupOpen(true);
    fetchData();
  };

  const handleDeleteClick = async (item) => {
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const handleDetailClick = (item) => {  
    fetchUserroles(item.id)
    setIsDetailPopupOpen(true);
    fetchData();
};

  const closePopups = () => {
    setIsEditPopupOpen(false);
    setIsArchivePopupOpen(false);
    setIsNewPopupOpen(false);
    setIsDetailPopupOpen(false)
    setIsDeletePopupOpen(false)
    setSelectedItem([])
    setPopupError('');
    
  };

  const tableHeaders = ['Permission', 'View','View All','Add', 'Edit', 'Archive', 'Delete'];

  const addPopupFields = [
    { label: 'User Role', name:'name', type: 'small-input', validators: [isRequired], defaultValue: '' },
  ];

  const editPopupFields = [
    { label: 'User Role', name: 'name', type: 'small-input',
       defaultValue: selectedItem?.name || '',validators: [isRequired]
      },
  ];
  
  const detailPopupFields = [
    { label: 'User Role','name':'name',  value: selectedItem?.name, type: 'small' },
    
  ]; 

  const deletePopupFields = [
    { label: 'Are you sure you want to delete this record? Once deleted, it cannot be recovered.', type: 'text' },
  ];

  const handleExportComplete = () => {
    resetCheckboxState();
  };


  return (

    <div className='whole-container'>  
      <div className='top-heading'>
        <PageHeader pageName="Subscription /" nestedpageName="User Roles"  linkTo="" /> 
        <div className='export-container'>
        <ArchiveSelectedItem
          selectedItems={selectedItems}
          items={items}
          onArchiveSelected={handleArchiveSelectedItems}
        />
          <ExportLabel
            exportUrl={`${baseurl}accounts/roles/export/`}
            selectedIds={selectedItems}
            onExportComplete={handleExportComplete}
          />
        </div>
      </div>     
      <div className='pagename-container'>
        <div className='page-name'>
          <h1 className='common-text fts-22 ftw-6 blue'>User Roles</h1>
        </div>
        <div className='pagename-right-container'>
          <AddButton onClick={handleNewClick} />
          <SimpleSearchBox placeholder="Search" onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term} />

          <Filter options={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption}/>
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption}/>
        </div>
      </div>
      <div className="table-container">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th className='w-80'>
                  <input
                    className="table-selectbox table-parent-selectbox"
                    type="checkbox"
                    checked={isParentSelected}
                    onChange={handleParentCheckboxChange}
                  />Name
              </th>
              <th className='w-20'>Actions</th>
            </tr>
          </thead>
          <tbody className="common-table-tbody">
            {items.length > 0 ? (
              items.map((role) => (
                <tr key={role.id} className={`${role.is_archived ? 'archived-item' : ''}`}>                
                  <td >
                    <div className='d-flex ai-center'>
                      <input
                        className="table-selectbox table-child-selectbox"
                        type="checkbox"
                        checked={selectedItems.includes(role.id)}
                        onChange={() => handleChildCheckboxChange(role.id)}
                      />
                      {role.name}
                    </div> 
                  </td>
                  <td className="actions">
                    <EditIcon onClick={() => handleEditClick(role)} />
                    <ArchiveIcon onClick={() => handleArchiveClick(role)} isArchived={role.is_archived} />
                    <DetailIcon onClick={() => handleDetailClick(role)} />
                    <DeleteIcon onClick={() => handleDeleteClick(role)} />
                  </td>
                  </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2">
                  No User role found.
                </td>
              </tr>
            )}
          </tbody>


        </table>
      </div>      

      <ArchivePopup
        isOpen={isArchivePopupOpen}
        isArchived={selectedItem?.is_archived }
        archivename={selectedItem?.name }
        onSave={handleArchive}
        onClose={() => setIsArchivePopupOpen(false)}
      />

      <EditInternalUserRolesPopup
        isOpen={isEditPopupOpen}
        title="Edit User Role"
        fields={editPopupFields}
        onSave={handleEdit}
        onClose={closePopups}
        tableHeaders={tableHeaders}
        initialTableRows={initialTableRows}
        selected={selectedItem}
        popupErrorMessage={popupError}
      />
      <AddUserRolePopup 
        isOpen={isNewPopupOpen}
        title="Add User Role"               
        buttonName='Add User Role'
        fields={addPopupFields}  
        onSave={AddUserRole}
        onClose={closePopups}
        tableHeaders={tableHeaders}
        initialTableRows={initialTableRows}
        popupErrorMessage={popupError}
      />
      <InternalUserDetailPopup
        isOpen={isDetailPopupOpen} 
        title="User Role Details"
        details={detailPopupFields}
        onClose={closePopups}
        tableHeaders={tableHeaders}
        tableRows={initialTableRows}
        selected={selectedItem}
      />
      <DeletePopup
        isOpen={isDeletePopupOpen}
        title="Delete User"
        fields={deletePopupFields}
        onSave={handleDelete}
        onClose={closePopups}
      /> 
    </div>
  );
};

export default UserRoles;
